import React, { useState, useEffect, useMemo } from 'react';
import { useTestMasters } from '../../hooks/useTestMasters';

const TestMasterModal = () => {
  const { 
    isModalOpen, 
    closeModal, 
    selectedTestMaster, 
    createTestMaster, 
    updateTestMaster,
    modalMode,
    allTestMasters
  } = useTestMasters();

  // State for template selection
  const [selectedTemplate, setSelectedTemplate] = useState('');
  
  // Group test masters by biomarker name for the template dropdown
  const biomarkerGroups = useMemo(() => {
    if (!allTestMasters || !Array.isArray(allTestMasters)) return [];
    
    // Group by biomarker_name
    const groups = {};
    allTestMasters.forEach(test => {
      if (test.biomarker_name) {
        if (!groups[test.biomarker_name]) {
          groups[test.biomarker_name] = [];
        }
        groups[test.biomarker_name].push(test);
      }
    });
    
    // Convert to array format for dropdown
    return Object.keys(groups).sort().map(name => ({
      name,
      tests: groups[name].sort((a, b) => {
        // Sort by gender and then by age range
        if (a.gender !== b.gender) return a.gender.localeCompare(b.gender);
        return (a.agerange_min || 0) - (b.agerange_min || 0);
      })
    }));
  }, [allTestMasters]);
  
  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplate(templateId);
    
    if (!templateId) return; // If empty selection, do nothing
    
    // Find the selected test master
    const template = allTestMasters.find(test => {
      // Check all possible ID fields
      return (test._id === templateId || 
              test.id === templateId || 
              test.biomarker_id === templateId ||
              String(test._id) === templateId || 
              String(test.id) === templateId || 
              String(test.biomarker_id) === templateId);
    });
    
    console.log('Selected template ID:', templateId);
    console.log('Found template:', template);
    
    if (!template) {
      console.error('Template not found with ID:', templateId);
      // Log all IDs for debugging
      console.log('Available IDs:', allTestMasters.map(t => ({ 
        _id: t._id, 
        id: t.id, 
        biomarker_id: t.biomarker_id,
        test_code: t.test_code
      })));
      return;
    }
    
    // Copy template data to form, but clear the ID and test_code to ensure it's a new entry
    const templateData = JSON.parse(JSON.stringify(template)); // Deep copy
    delete templateData._id;
    delete templateData.id;
    delete templateData.biomarker_id;
    
    console.log('Template data to apply:', templateData);
    
    // Update the form with template data
    setFormData({
      ...templateData,
      test_code: '', // Clear test code for new entry
    });
    
    console.log('Form data after update:', templateData);
  };
  
  const [formData, setFormData] = useState({
    test_code: '',
    biomarker_name: '',
    category: '',
    gender: '',
    hormonal_phase: '',
    test_name: '',
    description: '',
    agerange_min: '',
    agerange_max: '',
    unit: '',
    normal_range_min: '',
    normal_range_max: '',
    mildly_decreased_min: '',
    mildly_decreased_max: '',
    significantly_decreased_min: '',
    significantly_decreased_max: '',
    mildly_increased_min: '',
    mildly_increased_max: '',
    significantly_increased_min: '',
    significantly_increased_max: '',
    impact_on_health_decreased: '',
    impact_on_health_increased: '',
    suggestions_to_improve_when_decreased: '',
    suggestions_to_improve_when_increased: '',
    followuptime_mildly_deranged: '',
    followuptime_significantly_deranged: '',
    consider_of_health_score: '',
    test_need_attention: '',
    report_order: '',
    optimal_range_min: '',
    optimal_range_max: '',
    clinical_data_category: {},
    health_score_weightage: [],
    non_biomarker: false
  });

  // Initialize form data when modal opens or selectedTestMaster changes
  useEffect(() => {
    // Reset template selection whenever modal state changes
    setSelectedTemplate('');
    
    if (selectedTestMaster && modalMode === 'edit') {
      // Convert boolean fields from DB format to form format
      const consider_health_score = selectedTestMaster.consider_of_health_score === '\u0001' ? '1' : '';
      const need_attention = selectedTestMaster.test_need_attention === '\u0001' ? '1' : '';
      
      setFormData({
        test_code: selectedTestMaster.test_code || '',
        biomarker_name: selectedTestMaster.biomarker_name || '',
        category: selectedTestMaster.category === null ? '' : (selectedTestMaster.category || ''),
        gender: selectedTestMaster.gender || '',
        hormonal_phase: selectedTestMaster.hormonal_phase || '',
        test_name: selectedTestMaster.test_name || '',
        description: selectedTestMaster.description || '',
        agerange_min: selectedTestMaster.agerange_min !== null ? selectedTestMaster.agerange_min : '',
        agerange_max: selectedTestMaster.agerange_max !== null ? selectedTestMaster.agerange_max : '',
        unit: selectedTestMaster.unit || '',
        normal_range_min: selectedTestMaster.normal_range_min !== null ? selectedTestMaster.normal_range_min : '',
        normal_range_max: selectedTestMaster.normal_range_max !== null ? selectedTestMaster.normal_range_max : '',
        mildly_decreased_min: selectedTestMaster.mildly_decreased_min !== null ? selectedTestMaster.mildly_decreased_min : '',
        mildly_decreased_max: selectedTestMaster.mildly_decreased_max !== null ? selectedTestMaster.mildly_decreased_max : '',
        significantly_decreased_min: selectedTestMaster.significantly_decreased_min !== null ? selectedTestMaster.significantly_decreased_min : '',
        significantly_decreased_max: selectedTestMaster.significantly_decreased_max !== null ? selectedTestMaster.significantly_decreased_max : '',
        mildly_increased_min: selectedTestMaster.mildly_increased_min !== null ? selectedTestMaster.mildly_increased_min : '',
        mildly_increased_max: selectedTestMaster.mildly_increased_max !== null ? selectedTestMaster.mildly_increased_max : '',
        significantly_increased_min: selectedTestMaster.significantly_increased_min !== null ? selectedTestMaster.significantly_increased_min : '',
        significantly_increased_max: selectedTestMaster.significantly_increased_max !== null ? selectedTestMaster.significantly_increased_max : '',
        impact_on_health_decreased: selectedTestMaster.impact_on_health_decreased || '',
        impact_on_health_increased: selectedTestMaster.impact_on_health_increased || '',
        suggestions_to_improve_when_decreased: selectedTestMaster.suggestions_to_improve_when_decreased || '',
        suggestions_to_improve_when_increased: selectedTestMaster.suggestions_to_improve_when_increased || '',
        followuptime_mildly_deranged: selectedTestMaster.followuptime_mildly_deranged || '',
        followuptime_significantly_deranged: selectedTestMaster.followuptime_significantly_deranged || '',
        consider_of_health_score: consider_health_score,
        test_need_attention: need_attention,
        report_order: selectedTestMaster.report_order !== null ? selectedTestMaster.report_order : '',
        optimal_range_min: selectedTestMaster.optimal_range_min !== null ? selectedTestMaster.optimal_range_min : '',
        optimal_range_max: selectedTestMaster.optimal_range_max !== null ? selectedTestMaster.optimal_range_max : '',
        clinical_data_category: selectedTestMaster.clinical_data_category || {},
        health_score_weightage: (selectedTestMaster.health_score_weightage || []).map(item => ({
          ...item,
          associated_test_types: item.associated_test_types || ['PRO'],
          score_levels: item.score_levels || {
            optimal: 100,
            normal: 80,
            suboptimal: 60
          }
        })),
        non_biomarker: selectedTestMaster.non_biomarker || false
      });
    } else {
      // Reset form for create mode
      setFormData({
        test_code: '',
        biomarker_name: '',
        category: '',
        gender: '',
        hormonal_phase: '',
        test_name: '',
        description: '',
        agerange_min: '',
        agerange_max: '',
        unit: '',
        normal_range_min: '',
        normal_range_max: '',
        mildly_decreased_min: '',
        mildly_decreased_max: '',
        significantly_decreased_min: '',
        significantly_decreased_max: '',
        mildly_increased_min: '',
        mildly_increased_max: '',
        significantly_increased_min: '',
        significantly_increased_max: '',
        impact_on_health_decreased: '',
        impact_on_health_increased: '',
        suggestions_to_improve_when_decreased: '',
        suggestions_to_improve_when_increased: '',
        followuptime_mildly_deranged: '',
        followuptime_significantly_deranged: '',
        consider_of_health_score: '',
        test_need_attention: '',
        report_order: '',
        optimal_range_min: '',
        optimal_range_max: '',
        clinical_data_category: {},
        health_score_weightage: [],
        non_biomarker: false
      });
    }
  }, [selectedTestMaster, modalMode, isModalOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested properties (e.g., clinical_data_category.profile_name)
    if (name.includes('.')) {
      const [parentKey, childKey] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey],
          [childKey]: value
        }
      }));
    } else {
      // Handle regular properties
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create a processed data object that matches the exact DB format
    const processedFormData = { ...formData };
    
    // Handle numeric fields - convert strings to numbers
    const numericFields = [
      'agerange_min', 'agerange_max',
      'normal_range_min', 'normal_range_max',
      'optimal_range_min', 'optimal_range_max',
      'mildly_decreased_min', 'mildly_decreased_max',
      'significantly_decreased_min', 'significantly_decreased_max',
      'mildly_increased_min', 'mildly_increased_max',
      'significantly_increased_min', 'significantly_increased_max',
      'report_order'
    ];
    
    // Convert string values to numbers for numeric fields
    numericFields.forEach(field => {
      if (processedFormData[field] === '') {
        // Empty strings should be null for these fields
        processedFormData[field] = null;
      } else if (processedFormData[field] !== null && processedFormData[field] !== undefined) {
        processedFormData[field] = parseFloat(processedFormData[field]);
      }
    });
    
    // Handle boolean fields - convert to the format used in DB ('\u0001' for true, empty for false)
    const booleanFields = ['consider_of_health_score', 'test_need_attention'];
    booleanFields.forEach(field => {
      if (processedFormData[field] === '1') {
        processedFormData[field] = '\u0001'; // Unicode character used in DB for true
      } else if (processedFormData[field] === '' || processedFormData[field] === '0') {
        processedFormData[field] = ''; // Empty string for false
      }
    });
    
    // Handle category field - ensure it's null if empty
    if (processedFormData.category === '') {
      processedFormData.category = null;
    }
    
    // Handle clinical_data_category position field - convert to number
    if (processedFormData.clinical_data_category && processedFormData.clinical_data_category.position !== '') {
      processedFormData.clinical_data_category.position = parseInt(processedFormData.clinical_data_category.position);
    }
    
    // Handle empty clinical_data_category
    if (processedFormData.clinical_data_category && 
        !processedFormData.clinical_data_category.profile_name && 
        !processedFormData.clinical_data_category.position) {
      processedFormData.clinical_data_category = null;
    }
    
    // Handle health_score_weightage - filter out empty entries and preserve score_levels and associated_test_types
    if (processedFormData.health_score_weightage && processedFormData.health_score_weightage.length > 0) {
      processedFormData.health_score_weightage = processedFormData.health_score_weightage
        .filter(item => item.name && item.weight)
        .map(item => ({
          name: item.name,
          weight: parseInt(item.weight),
          associated_test_types: item.associated_test_types || ['PRO'],
          score_levels: item.score_levels || {
            optimal: 100,
            normal: 80,
            suboptimal: 60
          }
        }));
    }
    
    // Check if total weightage exceeds 100
    const totalWeightage = processedFormData.health_score_weightage.reduce(
      (sum, item) => sum + (parseInt(item.weight) || 0),
      0
    );
    
    if (totalWeightage > 100) {
      if (!window.confirm('Total Health Score Weightage exceeds 100. Are you sure you want to continue?')) {
        return; // Stop submission if user cancels
      }
    }
    
    try {
      if (modalMode === 'edit' && selectedTestMaster) {
        // Debug log to see what's in the selectedTestMaster
        console.log('Selected Test Master:', selectedTestMaster);
        console.log('Processed Form Data:', processedFormData);
        
        // Get the ID from the selectedTestMaster, checking both _id and id fields
        const testId = selectedTestMaster._id || selectedTestMaster.id;
        
        if (!testId) {
          console.error('Error: Missing test master ID for update');
          console.error('Selected Test Master:', selectedTestMaster);
          return;
        }
        
        console.log(`Updating test master with ID: ${testId}`);
        await updateTestMaster(testId, processedFormData);
      } else {
        console.log('Creating new test master with data:', processedFormData);
        await createTestMaster(processedFormData);
      }
      closeModal();
    } catch (error) {
      console.error('Error saving test master:', error);
    }
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            {modalMode === 'edit' ? 'Edit Test Master' : 'Create New Test Master'}
          </h2>
          <button 
            onClick={closeModal}
            className="text-gray-500 hover:text-gray-700"
          >
            &times;
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="max-h-[70vh] overflow-y-auto px-2">
          {/* Template Selection - Only show for new entries */}
          {modalMode === 'create' && (
            <div className="mb-6 border-b pb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Existing Test as Template (Optional)
              </label>
              <select
                value={selectedTemplate}
                onChange={handleTemplateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">-- Select a test to use as template --</option>
                {biomarkerGroups.map(group => (
                  <optgroup key={group.name} label={group.name}>
                    {group.tests.map(test => {
                      // Make sure we have a valid ID to use
                      const testId = test._id || test.id || test.biomarker_id;
                      console.log('Test option:', test.test_code, 'ID:', testId);
                      return (
                        <option key={testId} value={testId}>
                          {test.test_code} ({test.gender}, {test.agerange_min}-{test.agerange_max} yrs)
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                This will pre-fill the form with data from the selected test. You can then modify it as needed.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Code*
              </label>
              <input
                type="text"
                name="test_code"
                value={formData.test_code}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            
            <div className="md:col-span-2">
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Biomarker Name*
                  </label>
                  <input
                    type="text"
                    name="biomarker_name"
                    value={formData.biomarker_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center text-sm font-medium text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      name="non_biomarker"
                      checked={formData.non_biomarker}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          non_biomarker: e.target.checked
                        }));
                      }}
                      className="w-4 h-4 border border-gray-300 rounded mr-2"
                    />
                    Non-Biomarker
                  </label>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Select Category</option>
                <option value="blood">Blood</option>
                <option value="urine">Urine</option>
                <option value="imaging">Imaging</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender*
              </label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="both">Both</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hormonal Phase
              </label>
              <select
                name="hormonal_phase"
                value={formData.hormonal_phase}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={formData.gender == 'male'}
              >
                <option value="">Select Hormonal Phase</option>
                <option value="follicular_phase">Follicular phase</option>
                <option value="mid_cycle">Mid Cycle</option>
                <option value="luteal_phase">Luteal Phase</option>
                <option value="menopause_without_ht">Menopause without Hormone therapy</option>
                <option value="menopause_with_ht">Menopause with Hormone therapy</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unit
              </label>
              <input
                type="text"
                name="unit"
                value={formData.unit}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Map to Test
              </label>
              <input
                type="text"
                name="test_name"
                value={formData.test_name}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Enter test name to map to"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Order
              </label>
              <input
                type="number"
                name="report_order"
                value={formData.report_order}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Age Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Age*
              </label>
              <input
                type="number"
                name="agerange_min"
                value={formData.agerange_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Age*
              </label>
              <input
                type="number"
                name="agerange_max"
                value={formData.agerange_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Normal Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min*
              </label>
              <input
                type="number"
                step="any"
                name="normal_range_min"
                value={formData.normal_range_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max*
              </label>
              <input
                type="number"
                step="any"
                name="normal_range_max"
                value={formData.normal_range_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Optimal Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min
              </label>
              <input
                type="number"
                step="any"
                name="optimal_range_min"
                value={formData.optimal_range_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max
              </label>
              <input
                type="number"
                step="any"
                name="optimal_range_max"
                value={formData.optimal_range_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Mildly Decreased Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min
              </label>
              <input
                type="number"
                step="any"
                name="mildly_decreased_min"
                value={formData.mildly_decreased_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max
              </label>
              <input
                type="number"
                step="any"
                name="mildly_decreased_max"
                value={formData.mildly_decreased_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Significantly Decreased Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min
              </label>
              <input
                type="number"
                step="any"
                name="significantly_decreased_min"
                value={formData.significantly_decreased_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max
              </label>
              <input
                type="number"
                step="any"
                name="significantly_decreased_max"
                value={formData.significantly_decreased_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Mildly Increased Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min
              </label>
              <input
                type="number"
                step="any"
                name="mildly_increased_min"
                value={formData.mildly_increased_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max
              </label>
              <input
                type="number"
                step="any"
                name="mildly_increased_max"
                value={formData.mildly_increased_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Significantly Increased Range</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min
              </label>
              <input
                type="number"
                step="any"
                name="significantly_increased_min"
                value={formData.significantly_increased_min}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max
              </label>
              <input
                type="number"
                step="any"
                name="significantly_increased_max"
                value={formData.significantly_increased_max}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Description</h3>
          <div className="mb-4">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter biomarker description here"
            ></textarea>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Health Impact</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Impact When Decreased
            </label>
            <textarea
              name="impact_on_health_decreased"
              value={formData.impact_on_health_decreased}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Impact When Increased
            </label>
            <textarea
              name="impact_on_health_increased"
              value={formData.impact_on_health_increased}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            ></textarea>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Improvement Suggestions</h3>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suggestions When Decreased
            </label>
            <textarea
              name="suggestions_to_improve_when_decreased"
              value={formData.suggestions_to_improve_when_decreased}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            ></textarea>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suggestions When Increased
            </label>
            <textarea
              name="suggestions_to_improve_when_increased"
              value={formData.suggestions_to_improve_when_increased}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            ></textarea>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Follow-up Time</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mildly Deranged
              </label>
              <input
                type="text"
                name="followuptime_mildly_deranged"
                value={formData.followuptime_mildly_deranged}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Significantly Deranged
              </label>
              <input
                type="text"
                name="followuptime_significantly_deranged"
                value={formData.followuptime_significantly_deranged}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>

          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Additional Settings</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consider for Health Score
              </label>
              <select
                name="consider_of_health_score"
                value={formData.consider_of_health_score}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Test Needs Attention
              </label>
              <select
                name="test_need_attention"
                value={formData.test_need_attention}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">No</option>
                <option value="1">Yes</option>
              </select>
            </div>
          </div>
          
          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Clinical Data Category</h3>
          {formData.clinical_data_category ? (
            <div className="mb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Name
                  </label>
                  <select
                    name="clinical_data_category.profile_name"
                    value={formData.clinical_data_category?.profile_name || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        clinical_data_category: {
                          ...prev.clinical_data_category,
                          profile_name: e.target.value
                        }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select Profile</option>
                    <option value="Complete Blood Picture">Complete Blood Picture</option>
                    <option value="Lipid Profile">Lipid Profile</option>
                    <option value="Diabetes Profile">Diabetes Profile</option>
                    <option value="Thyroid Function">Thyroid Function</option>
                    <option value="Kidney Function">Kidney Function</option>
                    <option value="Liver Health">Liver Health</option>
                    <option value="Vitamins & Minerals">Vitamins & Minerals</option>
                    <option value="Hormone Health">Hormone Health</option>
                    <option value="Inflammation Markers">Inflammation Markers</option>
                    <option value="Specialty Tests">Specialty Tests</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position
                  </label>
                  <input
                    type="number"
                    value={formData.clinical_data_category?.position || ''}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        clinical_data_category: {
                          ...prev.clinical_data_category,
                          position: e.target.value
                        }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    clinical_data_category: null
                  }));
                }}
                className="mt-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors duration-200 border border-red-200"
              >
                Remove Clinical Data Category
              </button>
            </div>
          ) : (
            <div className="mb-4">
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    clinical_data_category: {
                      position: '',
                      profile_name: ''
                    }
                  }));
                }}
                className="px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md transition-colors duration-200 border border-blue-200"
              >
                Add Clinical Data Category
              </button>
            </div>
          )}
          
          <h3 className="text-lg font-medium text-gray-900 mb-2 border-b pb-1">Health Score Weightage</h3>
          <div className="mb-4">
            {formData.health_score_weightage.map((item, index) => (
              <div key={index} className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={item.name || ''}
                      onChange={(e) => {
                        const newWeightage = [...formData.health_score_weightage];
                        newWeightage[index] = { ...newWeightage[index], name: e.target.value };
                        setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                      }}
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">Select a category</option>
                      <option value="Metabolic Health">Metabolic Health</option>
                      <option value="Heart Health">Heart Health</option>
                      <option value="Cognition and Memory">Cognition and Memory</option>
                      <option value="Endurance and Fitness">Endurance and Fitness</option>
                      <option value="Immune Health">Immune Health</option>
                      <option value="Gut Health">Gut Health</option>
                      <option value="Inflammation">Inflammation</option>
                      <option value="Bone Health">Bone Health</option>
                      <option value="Female Hormones">Female Hormones</option>
                      <option value="Male Hormones">Male Hormones</option>
                      <option value="Sleep and Recovery">Sleep and Recovery</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Weight</label>
                    <input
                      type="number"
                      value={item.weight || ''}
                      onChange={(e) => {
                        const newValue = parseInt(e.target.value) || 0;
                        const newWeightage = [...formData.health_score_weightage];
                        newWeightage[index] = { ...newWeightage[index], weight: newValue };
                        setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                      }}
                      placeholder="Weight"
                      className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Associated Test Types</label>
                  <div className="flex gap-3">
                    {['PRO', 'BASIC', 'OTHER'].map((testType) => (
                      <label key={testType} className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={(item.associated_test_types || []).includes(testType)}
                          onChange={(e) => {
                            const newWeightage = [...formData.health_score_weightage];
                            const currentTypes = item.associated_test_types || [];
                            if (e.target.checked) {
                              newWeightage[index] = {
                                ...newWeightage[index],
                                associated_test_types: [...currentTypes, testType]
                              };
                            } else {
                              newWeightage[index] = {
                                ...newWeightage[index],
                                associated_test_types: currentTypes.filter(t => t !== testType)
                              };
                            }
                            setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                          }}
                          className="w-4 h-4 border border-gray-300 rounded mr-2"
                        />
                        {testType}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-2">Score Levels</label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="block text-xs text-green-700 font-medium mb-1">Optimal</label>
                      <input
                        type="number"
                        value={item.score_levels?.optimal || 100}
                        onChange={(e) => {
                          const newWeightage = [...formData.health_score_weightage];
                          newWeightage[index].score_levels = {
                            ...newWeightage[index].score_levels,
                            optimal: parseInt(e.target.value) || 0
                          };
                          setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                        }}
                        min="0"
                        max="100"
                        className="w-full px-2 py-2 border border-green-300 rounded-md text-sm text-center bg-green-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-blue-700 font-medium mb-1">Normal</label>
                      <input
                        type="number"
                        value={item.score_levels?.normal || 80}
                        onChange={(e) => {
                          const newWeightage = [...formData.health_score_weightage];
                          newWeightage[index].score_levels = {
                            ...newWeightage[index].score_levels,
                            normal: parseInt(e.target.value) || 0
                          };
                          setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                        }}
                        min="0"
                        max="100"
                        className="w-full px-2 py-2 border border-blue-300 rounded-md text-sm text-center bg-blue-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-orange-700 font-medium mb-1">Suboptimal</label>
                      <input
                        type="number"
                        value={item.score_levels?.suboptimal || 60}
                        onChange={(e) => {
                          const newWeightage = [...formData.health_score_weightage];
                          newWeightage[index].score_levels = {
                            ...newWeightage[index].score_levels,
                            suboptimal: parseInt(e.target.value) || 0
                          };
                          setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                        }}
                        min="0"
                        max="100"
                        className="w-full px-2 py-2 border border-orange-300 rounded-md text-sm text-center bg-orange-50"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const newWeightage = [...formData.health_score_weightage];
                      newWeightage.splice(index, 1);
                      setFormData(prev => ({ ...prev, health_score_weightage: newWeightage }));
                    }}
                    className="w-full px-3 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors duration-200 border border-red-200 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  health_score_weightage: [...prev.health_score_weightage, { 
                    name: '', 
                    weight: 0,
                    associated_test_types: ['PRO'],
                    score_levels: {
                      optimal: 100,
                      normal: 80,
                      suboptimal: 60
                    }
                  }]
                }));
              }}
              className="mt-2 px-4 py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors duration-200 border border-blue-200"
            >
              Add Health Score Category
            </button>
          </div>
          
          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              {modalMode === 'edit' ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestMasterModal;
